export const TEAMS_PURCHASE_SEATS_PARAM = "teams_purchase_seats";

export const TEAMS_ACTIVATION_POLL_INTERVAL_MS = 2_000;
export const TEAMS_ACTIVATION_WAIT_MS = 60_000;
export const TEAMS_ACTIVATION_REQUEST_TIMEOUT_MS = 10_000;

export const TEAMS_ACTIVATION_COPY = {
  stillWaiting:
    "Your payment went through, and we are still setting up your team. This can take a minute.",
  refused:
    "Your payment went through, but we couldn't open your new team. Try again, or write to support@ovou.com if this keeps happening.",
  tryAgain: "Try again",
};

/** How a wait for the team ends. */
export type TeamsActivationOutcome = "ready" | "late" | "refused";

const PENDING_TEAMS_ACTIVATION_KEY = "ovou_teams_activation_pending";

export const getTeamsPurchaseSeats = (search: string): number | null => {
  const seats = Number(
    new URLSearchParams(search).get(TEAMS_PURCHASE_SEATS_PARAM),
  );
  return Number.isInteger(seats) && seats > 0 ? seats : null;
};

/**
 * A Teams payment hands over with the parameter even when the seat count is
 * unknown (empty value): its presence alone means "wait for the team".
 */
export const isTeamsPurchaseArrival = (search: string): boolean =>
  new URLSearchParams(search).has(TEAMS_PURCHASE_SEATS_PARAM);

// The flag holds when it was set: a tab left mid-wait never runs the cleanup,
// and a stale flag would keep switching off the no-team logout.
export const markTeamsActivationPending = (now: number = Date.now()): void => {
  sessionStorage.setItem(PENDING_TEAMS_ACTIVATION_KEY, String(now));
};

export const clearTeamsActivationPending = (): void => {
  sessionStorage.removeItem(PENDING_TEAMS_ACTIVATION_KEY);
};

const TEAMS_ARRIVAL_PATH = "/auth/login-with-token";

/**
 * A Teams purchase lands here before the webhook may have created the team.
 * The arrival URL counts too, on the page a purchase lands on only: the
 * session check can answer before that page has had a chance to set the flag.
 */
export const isTeamsActivationPending = (now: number = Date.now()): boolean => {
  const markedAt = Number(sessionStorage.getItem(PENDING_TEAMS_ACTIVATION_KEY));
  const isFresh = markedAt > 0 && now - markedAt < TEAMS_ACTIVATION_WAIT_MS;
  const isArrival =
    window.location.pathname.endsWith(TEAMS_ARRIVAL_PATH) &&
    isTeamsPurchaseArrival(window.location.search);
  return isFresh || isArrival;
};

/** The server answered the activation check with a client error. */
export class TeamsActivationRefusedError extends Error {
  constructor(readonly status: number) {
    super(`Teams activation refused (${status})`);
    this.name = "TeamsActivationRefusedError";
  }
}

// A client error will not change by asking again (a missing permission, a
// rejected request); a network error, a timeout or a 5xx may. 408 and 429 are
// worth another try.
const isRefusal = (error: unknown): boolean =>
  error instanceof TeamsActivationRefusedError &&
  error.status !== 408 &&
  error.status !== 429;

const pause = (ms: number, signal: AbortSignal) =>
  new Promise<void>(resolve => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener("abort", done);
  });

// Rejects once `signal` aborts. A request that ignores its signal, such as one
// parked in the shared interceptor's token-refresh queue (which settles only
// on a successful refresh), still ends when its attempt is aborted.
const abandoned = (signal: AbortSignal) =>
  new Promise<never>((_resolve, reject) => {
    const fail = () => reject(new Error("Teams activation request abandoned"));
    if (signal.aborted) fail();
    else signal.addEventListener("abort", fail, { once: true });
  });

/**
 * Asks until the team exists, the wait runs out, the server refuses or
 * `signal` aborts. A slow request, a network error or a 5xx counts as not
 * ready yet; a client error ends the wait as refused. No request outlives the
 * deadline: each is aborted at whichever comes first, its own timeout or the
 * end of the wait.
 */
export const waitForTeamsActivation = async (
  ask: (signal: AbortSignal) => Promise<{ status: string }>,
  signal: AbortSignal,
): Promise<TeamsActivationOutcome> => {
  const deadline = Date.now() + TEAMS_ACTIVATION_WAIT_MS;
  while (!signal.aborted && Date.now() < deadline) {
    const attempt = new AbortController();
    const stop = () => attempt.abort();
    const timer = setTimeout(
      stop,
      Math.min(TEAMS_ACTIVATION_REQUEST_TIMEOUT_MS, deadline - Date.now()),
    );
    signal.addEventListener("abort", stop);
    try {
      const answer = await Promise.race([
        ask(attempt.signal),
        abandoned(attempt.signal),
      ]);
      if (answer.status === "ready") return "ready";
    } catch (error) {
      if (isRefusal(error)) return "refused";
    } finally {
      clearTimeout(timer);
      signal.removeEventListener("abort", stop);
    }
    const remaining = deadline - Date.now();
    if (remaining <= 0) break;
    await pause(Math.min(TEAMS_ACTIVATION_POLL_INTERVAL_MS, remaining), signal);
  }
  return "late";
};
